/**
 * Price book — the app-side face of THE price-resolution seam.
 *
 * Sources (weakest to strongest): base (empty in the app; the shared view's quotes),
 * custom (self-priced series), warehouse (EOD warehouse for matched symbols); then
 * settings.priceLinks re-points held symbols at another series.
 *
 * The pure composition + lookup rules live in lib/calc/priceBook.ts (re-exported
 * below) so the Pages Functions share view prices with the SAME rules. Only the
 * I/O (warehouse fetch) stays in this file.
 */
import type { CustomPrices, WarehouseLink } from './portfolioApi';
import { fetchWarehousePrices } from './symbolsApi';

export {
  alignQuoteUnits, appliedPriceLinks, composePriceBook, latestClose, tradeUnits, unpricedHeld, fillUnpriced,
} from './calc/priceBook';

/**
 * Fetch the warehouse series for held codes the book cannot price — by the
 * code itself, which since the one-ticker-form cutover (WORK.md item 43) IS
 * the EODHD source_code the warehouse is keyed on. Same soft-fail as
 * `loadWarehouseSeries`: a code the warehouse does not hold stays unpriced,
 * exactly as before.
 */
export function loadUnpricedSeries(tickers: string[]): Promise<CustomPrices> {
  return loadWarehouseSeries(Object.fromEntries(tickers.map((t) => [t, { ticker: t }])));
}

/**
 * Fetch every warehouse-linked symbol's daily series in parallel. Soft-fails per
 * symbol (an unreachable warehouse leaves the base store standing).
 */
export async function loadWarehouseSeries(
  links: Record<string, WarehouseLink> | undefined,
): Promise<CustomPrices> {
  const out: CustomPrices = {};
  await Promise.all(Object.entries(links ?? {}).map(async ([sym, link]) => {
    try {
      const series = await fetchWarehousePrices(link);
      if (Object.keys(series).length > 0) out[sym] = series;
    } catch { /* warehouse unreachable — base store stands */ }
  }));
  return out;
}
